<template>
    <div class="checkout">

        <div>
            <nav class="wizard">
                <div class="container">
                    <ul>
                        <!-- Page 1 -->
                        <router-link to="/" tag="li" exact>
                            <div class="circle"></div>
                            <span class="link-text">Bestill reise</span>
                        </router-link>

                        <!-- Page 2 -->
                        <router-link to="/reisende" tag="li" v-if="canGoToPage2">
                            <div class="circle"></div>
                            <span class="link-text">Reisende</span>
                        </router-link>
                        <li class="inactive" v-else>
                            <div class="circle"></div>
                            <span class="link-text">Reisende</span>
                        </li>

                        <!-- Page 3 -->
                        <router-link to="/tillegg" tag="li" v-if="canGoToPage3">
                            <div class="circle"></div>
                            <span class="link-text">Tillegg</span>
                        </router-link>
                        <li class="inactive" v-else>
                            <div class="circle"></div>
                            <span class="link-text">Tillegg</span>
                        </li>

                        <!-- Page 4 -->
                        <router-link to="/bekreft" tag="li" v-if="canGoToPage4">
                            <div class="circle"></div>
                            <span class="link-text">Bekreft</span>
                        </router-link>
                        <li class="inactive" v-else>
                            <div class="circle"></div>
                            <span class="link-text">Bekreft</span>
                        </li>

                        <!-- Page 5 -->
                        <li class="inactive">
                            <div class="circle"></div>
                            <span class="link-text">Kvittering</span>
                        </li>

                    </ul>
                </div>
            </nav>
        </div>

        <div class="container">

            <div class="loading" v-if="!isReady && failedLoadingData">
                <div class="split">
                    <div class="content">
                        <section class="section-white">
                            <h2 class="text-green">Kunne ikke laste inn skjema</h2>
                            <h3>Vennligst prøv igjen om 2 minutter.</h3>
                        </section>
                    </div>
                    <div class="overview"><!-- Blank --></div>
                </div>
            </div>

            <div class="loading" v-if="!isReady && !failedLoadingData">
                <div class="split">
                    <div class="content">
                        <section class="section-white">
                            <h2 class="text-green">Laster inn..</h2>
                            <h3>Vennligst vent imens vi laster inn valgmulighetene for denne reisen.</h3>
                            <Loading lazy />
                        </section>
                    </div>
                    <div class="overview"><!-- Blank --></div>
                </div>
            </div>

            <transition name="fade">
                <div class="split" v-if="isReady">

                    <!-- Form content -->
                    <div>
                        <transition name="fade">
                            <router-view/>
                        </transition>

                        <div class="mt-6">
                            <portal-target name="navButtons"></portal-target>
                        </div>
                    </div>

                    <!-- Sidebar -->
                    <div class="overview">
                        <portal-target name="overviewButtons"></portal-target>

                        <section class="section-outline">
                            <h3>Bestilling:</h3>

                            <div class="heading">Reise:</div>
                            <div class="text">{{ title }}</div>

                            <div class="heading">Avreise:</div>
                            <div class="text">{{ selectedStartDate.displayDate }}</div>

                            <div v-if="selectedStartDate.airport">
                                <div class="heading">Avreiseflyplass:</div>
                                <div class="text">{{ selectedStartDate.airport }}</div>
                            </div>
                            <div v-else>
                                <div class="heading">Avreisested:</div>
                                <div class="text">{{ $store.state.bookingData.tourTemplate.UserConditions[0].Name }}</div>
                            </div>

                            <div class="heading">Antall reisende:</div>
                            <div class="text">{{ numberOfTravellers }}</div>

                            <div class="heading">Antall dager:</div>
                            <div class="text">{{ selectedStartDate.days }}</div>

                            <div v-if="$store.getters.selectedRooms.length">
                                <h3>Reise:</h3>

                                <div>
                                    <div class="heading">Rom:</div>
                                    <div class="text bb-1 pb-15" v-for="room in selectedRooms">
                                        {{ room.amount }} x {{ room.name }}
                                        <div><strong>kr {{ room.amount * room.price | nok }}</strong></div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="$store.getters.hasAnyExtras">
                                <h3>Tillegg:</h3>

                                <div>
                                    <div class="text bb-1 pb-15" v-for="extraGroup in $store.getters.selectedExtrasSidebarPerson">
                                        <div v-if="extraGroup[0].pricePerPerson">
                                            {{extraGroup.length}} x {{extraGroup[0].raw.raw.Name}}
                                            <div><strong>kr {{ extraGroup.reduce((result, g) => result + g.amount * g.personCount, 0) | nok }}</strong></div>
                                        </div>
                                        <div v-else>
                                            {{extraGroup.length}} x {{extraGroup[0].raw.raw.Name}}
                                            <div><strong>kr {{ extraGroup[0].amount * extraGroup.length | nok }}</strong></div>
                                        </div>
                                    </div>

                                    <div class="text bb-1 pb-15" v-for="extraGroup in $store.getters.selectedExtrasSidebarRoom">
                                        <div>
                                            {{ extraGroup.length }} x {{ extraGroup[0].raw.raw.Name }}
                                            <div><strong>kr {{ extraGroup[0].amount * extraGroup.length | nok }}</strong></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <h3 class="text-green">Totalpris: {{ totalPrice | nok }}</h3>
                        </section>
                    </div>

                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import {mapGetters, mapState} from 'vuex'
import Loading from "../components/Loading.vue";

export default {
    name: "Checkout",
    components: {Loading},
    props: [
        "entry",
        "tourId",
        "startDateId",
        "title"
    ],
    created() {
        this.$store.commit('setTourStartDateId', parseInt(this.startDateId));
        this.$store.commit('setTourId', parseInt(this.tourId));
        // Load persisted state if it exists, then re-load the booking data (extras, available rooms etc)
        let key = `booking_state_${this.tourId}_${this.startDateId}`;
        if (localStorage.getItem(key)) {
            this.$store.commit('restoreState', {});
        }

        this.$store.dispatch('loadData', {
            tourId: this.$store.state.tourId,
            startDateId: this.$store.state.tourStartDateId,
        });

        // If the state was not already restored, this will "initialize" the persistance of the store.
        this.$store.commit('restoreState', {});
    },
    computed: {

        canGoToPage2: {
            cache: false,
            get() {
                return this.isReady ? this.$store.getters.canFitAllTravelers && this.$store.getters.selectedStartDate.capacity > 0 : false;
            }
        },
        canGoToPage3: {
            cache: false,
            get() {
                return this.isReady ? this.$store.getters.hasFilledAllTravelerInformation && this.canGoToPage2 : false;
            }
        },
        canGoToPage4: {
            cache: false,
            get() {
                return this.isReady ? this.$store.getters.allTravelersHasBeenAssignedARoom && this.canGoToPage3 : false;
            }
        },
        ...mapState([
            'isReady',
            'failedLoadingData',
        ]),
        ...mapGetters([
            'numberOfTravellers',
            'selectedStartDate',
            'selectedRooms',
            'totalPrice'
        ]),
    }
};
</script>
